"use client";

import { useLenis } from "lenis/react";
import { animate, motion, useMotionValue, useTransform } from "motion/react";
import { useEffect, useState } from "react";

import { Logo } from "@/components/shared/logo";

import { PRELOADER_STORAGE_KEY } from "../../_constants/preloader";
import { markIntroDone } from "../../_lib/intro";

// =============================================================================

const EASE = [0.76, 0, 0.24, 1] as const;

/**
 * First-visit intro: the logo draws itself while a counter runs to 100, then
 * the curtain lifts on the page.
 */
export function Preloader() {
  const lenis = useLenis();
  const [phase, setPhase] = useState<"loading" | "leaving" | "done">("loading");
  const count = useMotionValue(0);
  const rounded = useTransform(count, (value) =>
    String(Math.round(value)).padStart(3, "0")
  );
  const progress = useTransform(count, [0, 100], [0, 1]);

  useEffect(() => {
    if (document.documentElement.dataset.preloader === "skip") {
      markIntroDone();
      setPhase("done");
      return;
    }

    lenis?.stop();
    const controls = animate(count, 100, {
      duration: 1.5,
      ease: [0.65, 0, 0.35, 1],
      onComplete: () => {
        setPhase("leaving");
        markIntroDone();
        try {
          sessionStorage.setItem(PRELOADER_STORAGE_KEY, "1");
        } catch {
          // Storage disabled: the intro plays again on the next visit
        }
      },
    });
    return () => controls.stop();
  }, [count, lenis]);

  useEffect(() => {
    if (phase !== "loading") lenis?.start();
  }, [phase, lenis]);

  if (phase === "done") return null;

  return (
    <motion.div
      aria-hidden
      className="preloader fixed inset-0 z-[300] flex items-center justify-center bg-background"
      initial={{ clipPath: "inset(0% 0% 0% 0%)" }}
      animate={
        phase === "leaving"
          ? { clipPath: "inset(0% 0% 100% 0%)" }
          : { clipPath: "inset(0% 0% 0% 0%)" }
      }
      transition={{ duration: 0.9, ease: EASE }}
      onAnimationComplete={() => {
        if (phase === "leaving") setPhase("done");
      }}
    >
      <motion.div
        animate={phase === "leaving" ? { y: -60, opacity: 0 } : { y: 0 }}
        transition={{ duration: 0.6, ease: EASE }}
      >
        <Logo className="w-16 text-foreground" animated duration={0.75} />
      </motion.div>
      <div className="absolute right-6 bottom-6 font-mono text-sm text-muted-foreground tabular-nums sm:right-10 sm:bottom-10">
        <motion.span>{rounded}</motion.span>
      </div>
      <div className="absolute inset-x-6 bottom-[4.5rem] h-px origin-left bg-foreground/10 sm:inset-x-10">
        <motion.div
          className="h-full origin-left bg-foreground"
          style={{ scaleX: progress }}
        />
      </div>
    </motion.div>
  );
}

// =============================================================================
